import { Injectable } from '@angular/core';
import { APP_ICONS } from '../icons/app-icons';
import { ConfigStateService } from '@abp/ng.core';
import { map, switchMap, Observable, of, filter } from 'rxjs';
import { MetroNavItem } from '../components/metro-nav-menu/metro-nav-menu.component';
import { SelectedSommelierService } from './selected-sommelier.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private roleMenus: Record<string, (slug?: string) => MetroNavItem[]> = {
    admin: () => [
      { label: '::Home', path: '/', iconSvg: APP_ICONS.home },
      { label: '::Wineries', path: '/wineries', iconSvg: APP_ICONS.wineries },
      { label: '::Wines', path: '/wines', iconSvg: APP_ICONS.wine },
      { label: '::Sommeliers', path: '/sommeliers', iconSvg: APP_ICONS.users },
      { label: '::Orders', path: '/backoffice/orders', iconSvg: APP_ICONS.report },
      { label: '::Coupons', path: '/coupons', iconSvg: APP_ICONS.coupon },
      { label: '::PickupPoints', path: '/backoffice/pickuppoints', iconSvg: APP_ICONS.report },
      { label: '::Communications', path: '/communications', iconSvg: APP_ICONS.bell },
      {
        label: '::Reports',
        path: 'reports_menu',
        iconSvg: APP_ICONS.analytics,
        children: [
          { label: '::SettlementOfCommissions', path: '/settlement-commissions', iconSvg: APP_ICONS.clipboardText },
          { label: '::ColppyHealthCheck', path: '/colppy-healthcheck', iconSvg: APP_ICONS.report },
          { label: '::WineSales', path: '/wines-sales', iconSvg: APP_ICONS.report_sales }
        ]
      },
      {
        label: '::Settings',
        path: '#',
        iconSvg: APP_ICONS.settings,
        children: [
          { label: 'Roles', path: '/identity/roles', iconSvg: APP_ICONS.users },
          { label: 'Users', path: '/identity/users', iconSvg: APP_ICONS.users },
          { label: 'Setting Management', path: '/setting-management', iconSvg: APP_ICONS.report }
        ]
      }
    ],
    sommelier: (slug: string = '') => [
      { label: '::Home', path: `/${slug}`, iconSvg: APP_ICONS.home },
      { label: '::Catalog', path: `/${slug}/catalog`, iconSvg: APP_ICONS.wine },
      { label: '::MyPerformance', path: `/${slug}/my-numbers`, iconSvg: APP_ICONS.analytics },
      { label: '::Profile', path: `/${slug}/profile`, iconSvg: APP_ICONS.user }
    ],
    client: (slug: string = '') => [
      { label: '::Home', path: `/${slug}`, iconSvg: APP_ICONS.home },
      { label: '::Catalog', path: `/${slug}/catalog`, iconSvg: APP_ICONS.wine },
      { label: '::Orders', path: `/${slug}/orders`, iconSvg: APP_ICONS.report },
      { label: '::Profile', path: `/${slug}/profile`, iconSvg: APP_ICONS.user }
    ]
  };

  private mobileRoleMenus: Record<string, (slug?: string) => MetroNavItem[]> = {
    admin: () => [
      { label: '::Dashboard', path: '/', iconSvg: APP_ICONS.home },
      { label: '::Orders', path: '/backoffice/orders', iconSvg: APP_ICONS.report },
      {
        label: '::Tables',
        path: '#',
        id: 'tables',
        iconSvg: APP_ICONS.clipboardText,
        children: [
          { label: '::Wineries', path: '/wineries', iconSvg: APP_ICONS.wineries },
          { label: '::Wines', path: '/wines', iconSvg: APP_ICONS.wine },
          { label: '::Sommeliers', path: '/sommeliers', iconSvg: APP_ICONS.users },
          { label: '::Orders', path: '/backoffice/orders', iconSvg: APP_ICONS.report },
          { label: '::Coupons', path: '/coupons', iconSvg: APP_ICONS.coupon },
          { label: '::PickupPoints', path: '/backoffice/pickuppoints', iconSvg: APP_ICONS.report },
          { label: '::Communications', path: '/communications', iconSvg: APP_ICONS.bell }
        ]
      },
      {
        label: '::Reports',
        path: '#',
        iconSvg: APP_ICONS.analytics,
        id: 'reports',
        children: [
          { label: '::SettlementOfCommissions', path: '/settlement-commissions', iconSvg: APP_ICONS.clipboardText }
        ]
      },
      {
        label: '::Settings',
        path: '#',
        iconSvg: APP_ICONS.settings,
        id: 'settings',
        children: [
          { label: 'Roles', path: '/identity/roles', iconSvg: APP_ICONS.users },
          { label: 'Users', path: '/identity/users', iconSvg: APP_ICONS.users },
          { label: 'Setting Management', path: '/setting-management', iconSvg: APP_ICONS.report }
        ]
      }
    ]
  };

  constructor(
    private config: ConfigStateService,
    private selectedSommelierService: SelectedSommelierService
  ) { }

  getNavItems(): Observable<MetroNavItem[]> {
    return this.config.getOne$('currentUser').pipe(
      filter((user: any) => {
        // If there is a user (logged in), wait until roles are available
        // to avoid the flickering effect where only "Home" is shown initially.
        if (user && (!user.roles || user.roles.length === 0)) {
          return false;
        }
        return true;
      }),
      switchMap(user => {
        const roles = user?.roles?.map(r => r.toLowerCase()) ?? [];

        if (roles.includes('sommelier') || roles.includes('client')) {
          return this.selectedSommelierService.getSlug$().pipe(
            map(slug => {
              const role = roles.includes('sommelier') ? 'sommelier' : 'client';
              return this.roleMenus[role]?.(slug || '') ?? [];
            })
          );
        }

        for (const role of roles) {
          if (this.roleMenus[role]) {
            return of(this.roleMenus[role]());
          }
        }

        return of([{ label: '::Home', path: '/', iconSvg: APP_ICONS.home }]);
      })
    );
  }

  getMobileNavItems(): Observable<MetroNavItem[]> {
    return this.config.getOne$('currentUser').pipe(
      filter((user: any) => {
        // Same logic for mobile items
        if (user && (!user.roles || user.roles.length === 0)) {
          return false;
        }
        return true;
      }),
      switchMap(user => {
        const roles = user?.roles?.map(r => r.toLowerCase()) ?? [];

        // usa menú mobile especial para admin
        if (roles.includes('admin') && this.mobileRoleMenus.admin) {
          return of(this.mobileRoleMenus.admin());
        }

        // caso sommelier/cliente → usa el mismo menú normal
        if (roles.includes('sommelier') || roles.includes('client')) {
          return this.selectedSommelierService.getSlug$().pipe(
            map(slug => {
              const role = roles.includes('sommelier') ? 'sommelier' : 'client';
              return this.roleMenus[role]?.(slug || '') ?? [];
            })
          );
        }

        // fallback
        return of(this.roleMenus[roles[0]]?.() ?? []);
      })
    );
  }
}
